import React, { useState } from 'react';
import { 
  Building2, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Settings, 
  Users, 
  FileText, 
  Calendar, 
  Tag, 
  Send, 
  MessageSquare, 
  AlertTriangle, 
  TrendingUp, 
  Percent, 
  DollarSign, 
  CheckCheck, 
  Filter, 
  Search, 
  RefreshCw,
  Eye,
  ChevronRight,
  Plus,
  Trash2,
  Share2,
  Sparkles
} from 'lucide-react';
import { 
  CashbackCompany, 
  CashbackRequest, 
  CashbackTransaction, 
  CashbackCampaign, 
  CashbackRules 
} from './CashbackTypes';

interface CashbackMerchantPanelProps {
  company: CashbackCompany;
  requests: CashbackRequest[];
  transactions: CashbackTransaction[];
  campaigns: CashbackCampaign[];
  onApproveRequest: (requestId: string) => void;
  onRejectRequest: (requestId: string, reason: string) => void;
  onBatchApprove: (requestIds: string[]) => void;
  onSaveRules: (companyId: string, rules: CashbackRules) => void;
  onCreateCampaign: (campaign: any) => void;
}

export const CashbackMerchantPanel: React.FC<CashbackMerchantPanelProps> = ({
  company,
  requests,
  transactions,
  campaigns,
  onApproveRequest,
  onRejectRequest,
  onBatchApprove,
  onSaveRules,
  onCreateCampaign
}) => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'requests' | 'rules' | 'validity' | 'campaigns' | 'customers' | 'reports'>('dashboard');

  // Filter Requests for this company
  const companyRequests = requests.filter(r => r.business_id === company.id);
  const pendingRequests = companyRequests.filter(r => r.status === 'pending');

  // Batch Selection
  const [selectedRequestIds, setSelectedRequestIds] = useState<string[]>([]);
  const [rejectModalId, setRejectModalId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null);

  // Form State for Rules Editing
  const [rules, setRules] = useState<CashbackRules>({ ...company.settings });
  const [rulesSavedSuccess, setRulesSavedSuccess] = useState(false);

  // Campaign Form Modal
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    title: '',
    description: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
    cashback_percent: 15,
    fixed_bonus: 0,
    target_audience: 'all' as 'all' | 'first_time' | 'vip' | 'birthday'
  });

  // Calculate Dashboard Metrics
  const totalApproved = companyRequests.filter(r => r.status === 'approved');
  const totalSalesVolume = totalApproved.reduce((acc, r) => acc + r.purchase_amount, 0);
  const totalCashbackGranted = totalApproved.reduce((acc, r) => acc + r.calculated_cashback, 0);
  const totalCashbackPending = pendingRequests.reduce((acc, r) => acc + r.calculated_cashback, 0);
  const totalCashbackUsed = Math.abs(
    transactions
      .filter(t => t.business_id === company.id && (t.type === 'spend' || t.status === 'used'))
      .reduce((acc, t) => acc + t.amount, 0)
  );

  // Re-engagement WhatsApp list (customers with cashback expiring in <= 15 days)
  const expiringCustomers = [
    { name: 'Carlos Eduardo Silva', phone: company.whatsapp || '5516992091604', balance: 10.00, expires_in_days: 5 },
    { name: 'Fernanda Lima', phone: company.whatsapp || '5516992091604', balance: 18.50, expires_in_days: 8 },
    { name: 'Mariana Costa', phone: company.whatsapp || '5516992091604', balance: 25.00, expires_in_days: 12 }
  ];

  const handleToggleSelectRequest = (id: string) => {
    setSelectedRequestIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllPending = () => {
    if (selectedRequestIds.length === pendingRequests.length) {
      setSelectedRequestIds([]);
    } else {
      setSelectedRequestIds(pendingRequests.map(r => r.id));
    }
  };

  const handleBatchApproveClick = () => {
    if (selectedRequestIds.length === 0) return;
    onBatchApprove(selectedRequestIds);
    setSelectedRequestIds([]);
    alert('Solicitações aprovadas em lote com sucesso!');
  };

  const handleSaveRulesSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSaveRules(company.id, rules);
    setRulesSavedSuccess(true);
    setTimeout(() => setRulesSavedSuccess(false), 2000);
  };

  const handleSendWhatsappReminder = (cust: { name: string; phone: string; balance: number; expires_in_days: number }) => {
    const text = encodeURIComponent(
      `👋 Olá ${cust.name}! Seu saldo de Cashback na ${company.name} no valor de R$ ${cust.balance.toFixed(2)} está prestes a expirar em ${cust.expires_in_days} dias! Aproveite e venha nos visitar para utilizar o seu desconto na sua próxima compra.`
    );
    window.open(`https://wa.me/${cust.phone}?text=${text}`, '_blank');
  };

  return (
    <div className="w-full space-y-6">
      {/* COMPANY HEADER */}
      <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/80 shadow-md flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <img
            src={company.logo_url}
            alt={company.name}
            className="w-16 h-16 rounded-2xl object-cover border border-slate-200 shadow-sm shrink-0"
          />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-extrabold uppercase text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full">
                Painel da Empresa
              </span>
              <span className="text-xs font-bold text-slate-400">
                Plano {company.plan.toUpperCase()}
              </span>
            </div>
            <h1 className="text-2xl font-black text-slate-800 tracking-tight mt-1">
              {company.name}
            </h1>
            <p className="text-xs text-slate-500">
              {company.category} • {company.city_state}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setActiveTab('requests')}
            className="relative px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-md transition-all active:scale-95 cursor-pointer flex items-center gap-2"
          >
            <Clock size={16} /> Aprovar Solicitações
            {pendingRequests.length > 0 && (
              <span className="bg-rose-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full animate-bounce">
                {pendingRequests.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* NAVIGATION TABS */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-slate-200 no-scrollbar">
        {[
          { id: 'dashboard', label: 'Dashboard Executivo', icon: TrendingUp },
          { id: 'requests', label: `Solicitações (${pendingRequests.length})`, icon: Clock },
          { id: 'rules', label: 'Regras de Cashback', icon: Settings },
          { id: 'validity', label: 'Validade & WhatsApp Lembretes', icon: MessageSquare },
          { id: 'campaigns', label: 'Campanhas', icon: Tag },
          { id: 'customers', label: 'Clientes & Extrato', icon: Users }
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap shrink-0 cursor-pointer ${
                isActive
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* TAB 1: EXECUTIVE DASHBOARD */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-sm">
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">
                Total de Vendas Geradas
              </span>
              <div className="text-2xl font-black text-slate-800 mt-1">
                R$ {totalSalesVolume.toFixed(2).replace('.', ',')}
              </div>
              <p className="text-[11px] text-emerald-600 font-bold mt-1">
                Com base em notas aprovadas
              </p>
            </div>

            <div className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-sm">
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">
                Cashback Concedido
              </span>
              <div className="text-2xl font-black text-emerald-600 mt-1">
                R$ {totalCashbackGranted.toFixed(2).replace('.', ',')}
              </div>
              <p className="text-[11px] text-slate-500 font-bold mt-1">
                Taxa média de {company.settings.default_percent}%
              </p>
            </div>

            <div className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-sm">
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">
                Cashback Reutilizado
              </span>
              <div className="text-2xl font-black text-indigo-600 mt-1">
                R$ {totalCashbackUsed.toFixed(2).replace('.', ',')}
              </div>
              <p className="text-[11px] text-indigo-600 font-bold mt-1">
                Clientes retornando à loja
              </p>
            </div>

            <div className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-sm">
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">
                Pendentes para Análise
              </span>
              <div className="text-2xl font-black text-amber-500 mt-1">
                {pendingRequests.length} Notas
              </div>
              <p className="text-[11px] text-amber-600 font-bold mt-1">
                R$ {totalCashbackPending.toFixed(2)} acumulados
              </p>
            </div>
          </div>

          {/* QUICK PENDING CALLOUT */}
          {pendingRequests.length > 0 && (
            <div className="bg-indigo-900 rounded-3xl p-6 text-white shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-500/30 text-indigo-200 rounded-2xl shrink-0">
                  <Clock size={24} />
                </div>
                <div>
                  <h3 className="text-base font-black uppercase tracking-wide">
                    Você possui {pendingRequests.length} solicitações de cashback aguardando aprovação
                  </h3>
                  <p className="text-xs text-indigo-200 mt-0.5">
                    Aproveite a função de aprovação em lote para liberar os saldos dos seus clientes instantaneamente.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActiveTab('requests')}
                className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-2xl transition-all shrink-0 cursor-pointer"
              >
                Analisar Agora
              </button>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: REQUESTS APPROVAL & BATCH ACTION */}
      {activeTab === 'requests' && (
        <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
            <div>
              <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                <Clock className="text-indigo-600" size={20} /> Análise & Aprovação de Notas Fiscais
              </h3>
              <p className="text-xs text-slate-500">
                Valide os comprovantes enviados pelos clientes para creditar o saldo
              </p>
            </div>

            {pendingRequests.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSelectAllPending}
                  className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs uppercase rounded-xl transition-colors"
                >
                  {selectedRequestIds.length === pendingRequests.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                </button>
                <button
                  onClick={handleBatchApproveClick}
                  disabled={selectedRequestIds.length === 0}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black text-xs uppercase rounded-xl transition-all flex items-center gap-1.5 shadow-md"
                >
                  <CheckCheck size={16} /> Aprovar Selecionados ({selectedRequestIds.length})
                </button>
              </div>
            )}
          </div>

          {companyRequests.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p className="text-xs font-bold">Nenhuma solicitação enviada para esta empresa até o momento.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {companyRequests.map(req => (
                <div
                  key={req.id}
                  className={`p-5 rounded-2xl border transition-all ${
                    req.status === 'pending'
                      ? 'border-indigo-200 bg-indigo-50/20'
                      : 'border-slate-100 bg-white'
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      {req.status === 'pending' && (
                        <input
                          type="checkbox"
                          checked={selectedRequestIds.includes(req.id)}
                          onChange={() => handleToggleSelectRequest(req.id)}
                          className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                        />
                      )}

                      <img
                        src={req.receipt_photo}
                        alt="Foto Nota"
                        onClick={() => setPreviewPhotoUrl(req.receipt_photo)}
                        className="w-16 h-16 rounded-xl object-cover border border-slate-200 shadow-sm cursor-pointer hover:scale-105 transition-transform shrink-0"
                      />

                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-black text-slate-800">{req.user_name}</h4>
                          <span className="text-[10px] text-slate-400 font-mono">({req.user_phone})</span>
                        </div>

                        <div className="text-xs text-slate-600 mt-0.5 space-x-3">
                          <span><strong>Cupom:</strong> {req.receipt_number}</span>
                          <span><strong>Data:</strong> {req.purchase_date}</span>
                          <span><strong>Valor Compra:</strong> R$ {req.purchase_amount.toFixed(2)}</span>
                        </div>

                        <div className="mt-1">
                          <span className="text-xs font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                            Cashback Calculado: R$ {req.calculated_cashback.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 justify-end">
                      {req.status === 'pending' ? (
                        <>
                          <button
                            onClick={() => onApproveRequest(req.id)}
                            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-sm flex items-center gap-1"
                          >
                            <CheckCircle2 size={14} /> Aprovar
                          </button>
                          <button
                            onClick={() => setRejectModalId(req.id)}
                            className="px-4 py-2 bg-rose-50 hover:bg-rose-100 text-rose-600 font-bold text-xs uppercase tracking-wider rounded-xl transition-colors cursor-pointer flex items-center gap-1"
                          >
                            <XCircle size={14} /> Rejeitar
                          </button>
                        </>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          req.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                        }`}>
                          {req.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 3: RULES CONFIGURATION */}
      {activeTab === 'rules' && (
        <form onSubmit={handleSaveRulesSubmit} className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/80 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div>
              <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                <Settings className="text-indigo-600" size={20} /> Regras de Concessão de Cashback
              </h3>
              <p className="text-xs text-slate-500">
                Configure os percentuais, compras mínimas e bônus da sua empresa
              </p>
            </div>
            {rulesSavedSuccess && (
              <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full animate-fade-in">
                ✓ Regras Salvas com Sucesso!
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-xs font-bold uppercase text-slate-700 mb-2">
                Percentual Padrão (%) *
              </label>
              <input
                type="number"
                step="0.1"
                required
                value={rules.default_percent}
                onChange={(e) => setRules({ ...rules, default_percent: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800"
              />
              <p className="text-[10px] text-slate-400 mt-1">Ex: 10% de retorno em todas as compras</p>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-slate-700 mb-2">
                Valor Mínimo da Compra (R$)
              </label>
              <input
                type="number"
                step="1"
                value={rules.min_purchase}
                onChange={(e) => setRules({ ...rules, min_purchase: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800"
              />
              <p className="text-[10px] text-slate-400 mt-1">Compra mínima para gerar cashback</p>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-slate-700 mb-2">
                Teto Máximo de Cashback por Compra (R$)
              </label>
              <input
                type="number"
                step="1"
                value={rules.max_cashback_per_purchase}
                onChange={(e) => setRules({ ...rules, max_cashback_per_purchase: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800"
              />
              <p className="text-[10px] text-slate-400 mt-1">Limite máximo retornado por cupom</p>
            </div>
          </div>

          {/* ADVANCED RULES */}
          <div className="pt-4 border-t border-slate-100 space-y-4">
            <h4 className="text-xs font-black uppercase tracking-wider text-indigo-900">
              Bônus Especiais & Fidelização
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100">
                <label className="block text-xs font-bold uppercase text-indigo-900 mb-1">
                  1ª Compra do Cliente (%)
                </label>
                <input
                  type="number"
                  value={rules.first_purchase_bonus_percent || 0}
                  onChange={(e) => setRules({ ...rules, first_purchase_bonus_percent: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-white border border-indigo-200 rounded-xl text-xs font-bold text-slate-800"
                />
                <p className="text-[10px] text-indigo-600 mt-1">Aumente para atrair novos clientes</p>
              </div>

              <div className="p-4 bg-purple-50/50 rounded-2xl border border-purple-100">
                <label className="block text-xs font-bold uppercase text-purple-900 mb-1">
                  Mês do Aniversário (%)
                </label>
                <input
                  type="number"
                  value={rules.birthday_bonus_percent || 0}
                  onChange={(e) => setRules({ ...rules, birthday_bonus_percent: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-white border border-purple-200 rounded-xl text-xs font-bold text-slate-800"
                />
                <p className="text-[10px] text-purple-600 mt-1">Ative presente de aniversário</p>
              </div>

              <div className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-100">
                <label className="block text-xs font-bold uppercase text-emerald-900 mb-1">
                  Clientes VIP (%)
                </label>
                <input
                  type="number"
                  value={rules.vip_bonus_percent || 0}
                  onChange={(e) => setRules({ ...rules, vip_bonus_percent: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-white border border-emerald-200 rounded-xl text-xs font-bold text-slate-800"
                />
                <p className="text-[10px] text-emerald-600 mt-1">Bônus para clientes recorrentes</p>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg transition-all cursor-pointer"
          >
            Salvar Regras de Cashback
          </button>
        </form>
      )}

      {/* TAB 4: VALIDITY & WHATSAPP RE-ENGAGEMENT */}
      {activeTab === 'validity' && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm">
            <h3 className="text-base font-black text-slate-800 uppercase tracking-wide mb-1 flex items-center gap-2">
              <MessageSquare className="text-emerald-600" size={20} /> Validade do Cashback & Reativação no WhatsApp
            </h3>
            <p className="text-xs text-slate-500">
              Envie lembretes com 1 clique para clientes que possuem saldo prestes a expirar para estimular o retorno.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm space-y-4">
            <h4 className="text-xs font-black uppercase text-slate-700 tracking-wider">
              Clientes com Saldo Preste a Expirar nos Próximos 15 Dias
            </h4>

            <div className="space-y-3">
              {expiringCustomers.map((cust, idx) => (
                <div key={idx} className="p-4 rounded-2xl border border-slate-100 bg-slate-50 flex items-center justify-between gap-4">
                  <div>
                    <h5 className="text-xs font-black text-slate-800">{cust.name}</h5>
                    <p className="text-[11px] text-slate-500">
                      Saldo prestes a expirar: <strong className="text-amber-600">R$ {cust.balance.toFixed(2)}</strong> em {cust.expires_in_days} dias
                    </p>
                  </div>

                  <button
                    onClick={() => handleSendWhatsappReminder(cust)}
                    className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer shrink-0"
                  >
                    <Send size={14} /> Enviar Lembrete no WhatsApp
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PHOTO PREVIEW MODAL */}
      {previewPhotoUrl && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-4 max-w-lg w-full relative shadow-2xl">
            <button
              onClick={() => setPreviewPhotoUrl(null)}
              className="absolute top-4 right-4 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold p-2 rounded-full"
            >
              ✕
            </button>
            <img src={previewPhotoUrl} alt="Comprovante Ampliado" className="w-full max-h-[70vh] object-contain rounded-2xl" />
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {rejectModalId && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="text-sm font-black text-slate-800 uppercase">Motivo da Rejeição</h3>
            <textarea
              rows={3}
              placeholder="Ex: Imagem ilegível ou data fora da validade."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs"
            ></textarea>

            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setRejectModalId(null)}
                className="px-4 py-2 bg-slate-100 text-slate-600 font-bold text-xs uppercase rounded-xl"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  onRejectRequest(rejectModalId, rejectReason || 'Comprovante não aceito.');
                  setRejectModalId(null);
                  setRejectReason('');
                }}
                className="px-4 py-2 bg-rose-600 text-white font-bold text-xs uppercase rounded-xl"
              >
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
